// useSwarmHistory — recent Slip Analysis sessions for the signed-in user.
//
// Slice D — the swarm persisted swarm_sessions/swarm_legs/swarm_verdicts but had
// NO history surface like the Extractor's. This hook mirrors useExtractionHistory
// for the swarm so a user can see and resume past analyses (resume = navigate to
// /swarm?session=<id>, which SwarmPage's loader picks up). RLS-gated to own rows.

import { useCallback, useEffect, useRef, useState } from "react";

import { useAuthSession } from "../../auth/useAuthSession.ts";
import { getBrowserClient } from "../../supabase/browser.ts";

export interface SwarmHistoryRow {
  id: string;
  leg_count: number | null;
  combined_odds: number | null;
  status: string | null;
  source_hint: string | null;
  bookmaker: string | null;
  created_at: string;
}

const FETCH_LIMIT = 50;

export interface UseSwarmHistoryState {
  rows: SwarmHistoryRow[];
  loading: boolean;
  refresh: () => void;
}

export function useSwarmHistory(): UseSwarmHistoryState {
  const { user } = useAuthSession();
  const userId = user?.id ?? null;
  const [rows, setRows] = useState<SwarmHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    const seq = ++seqRef.current;
    if (!userId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await getBrowserClient()
        .from("swarm_sessions" as never)
        .select("id,leg_count,combined_odds,status,source_hint,bookmaker,created_at")
        .eq("user_id" as never, userId as never)
        .order("created_at" as never, { ascending: false })
        .limit(FETCH_LIMIT);
      if (seq !== seqRef.current) return;
      setRows((data ?? []) as unknown as SwarmHistoryRow[]);
    } catch {
      if (seq === seqRef.current) setRows([]);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  return { rows, loading, refresh: () => void load() };
}
